import React, { useState, useEffect } from 'react';
import Modal from '../../../components/Modal';
import { API_BASE } from '../utils/constants';
import {
  formatCalendarDate,
  formatDismissalText,
  formatInstant,
  ptoBalanceText,
} from '../utils/dismissalReport';

export default function DismissedTab({ user }) {
  const [dismissals, setDismissals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/api/admin/dismissals`, {
        headers: {
          'Content-Type': 'application/json',
          'X-User-Email': user?.email || '',
        },
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || `Failed to load dismissals: ${res.status}`);
      }
      setDismissals(data.dismissals || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.email) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const cancelDismissal = async (dismissal) => {
    const confirmed = window.confirm(
      `Cancel the scheduled dismissal for ${dismissal.employee_email}? They will stay on the roster.`
    );
    if (!confirmed) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/dismissals/${encodeURIComponent(dismissal.id)}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Email': user?.email || '',
        },
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to cancel');
      }
      await load();
    } catch (e) {
      alert(e.message);
    }
  };

  const displayName = (row) => {
    const name = `${row.first_name || ''} ${row.last_name || ''}`.trim();
    return name || 'Not recorded';
  };

  if (loading) {
    return <div style={{ padding: 20 }}>Loading dismissals...</div>;
  }
  if (error) {
    return <div style={{ color: 'red', padding: 20 }}>{error}</div>;
  }

  return (
    <>
      <h2 className="content-title">Dismissed</h2>
      <p style={{ color: 'var(--symbolica-muted)', maxWidth: 720, marginTop: 0 }}>
        A scheduled last day keeps the person active through that calendar date in their office
        (London, San Francisco, or Australia). They move here at midnight at the start of the next
        day there, with the PTO balance from the last day. People who left before this was tracked
        are listed from their remaining time-off records. Blank dates and balances were not captured then.
      </p>
      {dismissals.length === 0 ? (
        <div style={{ color: 'var(--symbolica-muted)', padding: '1rem 0' }}>No dismissed employees.</div>
      ) : (
        <div className="table-scroll">
          <table className="emp-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Last day</th>
                <th>Moves</th>
                <th>Status</th>
                <th>PTO balance</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {dismissals.map((row) => {
                const migrated = row.source === 'migrated';
                const scheduled = row.status === 'scheduled';
                return (
                  <tr key={row.id}>
                    <td>{displayName(row)}</td>
                    <td>{row.employee_email}</td>
                    <td>{formatCalendarDate(row.last_day)}</td>
                    <td>
                      {scheduled
                        ? formatInstant(row.effective_at, row.timezone)
                        : migrated
                          ? 'Not recorded'
                          : formatInstant(row.dismissed_at || row.effective_at, row.timezone)}
                    </td>
                    <td>{scheduled ? 'Scheduled' : 'Dismissed'}</td>
                    <td>{ptoBalanceText(row)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {row.report && (
                          <button
                            className="secondary-button"
                            style={{ fontSize: 12, padding: '4px 8px' }}
                            onClick={() => setReport(row)}
                          >
                            View report
                          </button>
                        )}
                        {scheduled && (
                          <button
                            className="secondary-button"
                            style={{ fontSize: 12, padding: '4px 8px' }}
                            onClick={() => cancelDismissal(row)}
                          >
                            Cancel
                          </button>
                        )}
                        {migrated && !row.report && (
                          <span style={{ color: 'var(--symbolica-muted)', fontSize: 12 }}>No report</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {report && (
        <Modal isOpen={!!report} onClose={() => setReport(null)}>
          <h3 style={{ marginTop: 0 }}>
            Dismissal report
          </h3>
          <div style={{ color: 'var(--symbolica-muted)', marginBottom: 12, fontSize: 14 }}>
            {displayName(report)} ({report.employee_email})
            {report.last_day ? ` · last day ${formatCalendarDate(report.last_day)}` : ''}
            {report.office_label ? ` · ${report.office_label}` : ''}
          </div>
          <pre style={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 13,
            lineHeight: 1.7,
            whiteSpace: 'pre-wrap',
            background: '#F6F4F0',
            border: '1px solid #e5e7eb',
            borderRadius: 6,
            padding: 16,
            margin: 0,
          }}>
            {formatDismissalText(report.report)}
          </pre>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
            <button
              className="secondary-button"
              onClick={() => navigator.clipboard.writeText(formatDismissalText(report.report))}
            >
              Copy
            </button>
            <button className="primary-button" onClick={() => setReport(null)}>Close</button>
          </div>
        </Modal>
      )}
    </>
  );
}
